import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ErrorMessage } from '../../domain/error/error-message.model';
import { decodeError } from '../../domain/error/error.util';
import { getOneAndWaitforLoaded, onlyWhenOnline } from '../../shared/util/operators';
import { getQueryParametersFromURL } from '../../shared/util/util';
import { authUserLoadedGuest, authUserLoadedHard, authUserLoadedSoft } from '../auth/auth.actions';
import { selectAuthLoginState } from '../auth/auth.selectors';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LogService } from '../log/log.service';
import { createHardLoginSession, createHardLoginSessionFailure, createHardLoginSessionSuccess, createSession, createSessionFailure, createSessionSuccess, regenerateSession } from './session.actions';
import { selectSessionCreating, selectSessionID } from './session.selectors';
import { SessionService } from './session.service';
import { TranslateService } from '@ngx-translate/core';
import { ClickLog, ClickOperation } from '../clicks/click.models';
import { saveClickLog } from '../clicks/click.actions';

@Injectable({ providedIn: 'root' })
export class SessionEffects {
	createSession$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createSession),
			getOneAndWaitforLoaded(this.store),
			tap((action) => this.logService.info('Effect: createSession', action)),
			concatLatestFrom(() => this.store.select(selectAuthLoginState)),
			switchMap(([action, authLoginState]) =>
				this.sessionService
					.CreateSession(
						action.params,
						authLoginState,
						action.oldSessionID,
						action.url,
						action.referrer
					)
					.pipe(
						map((sessionData) => createSessionSuccess({ sessionID: sessionData.sessionID })),
						catchError((error: any) => {
							const errorMessage: ErrorMessage = decodeError(error, this.translate);
							return of(createSessionFailure({ error: errorMessage }));
						})
					)
			)
		)
	);

	createSessionSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createSessionSuccess),
				tap((sessionData) => {
					this.logService.info('Effect: createSessionSuccess');
					this.storageService.setItem('SESSIONID', sessionData.sessionID);
				})
			),
		{ dispatch: false }
	);

	createFailure = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createSessionFailure),
				tap(() => this.logService.info('Effect: createSessionFailure', undefined))
			),
		{ dispatch: false }
	);

  createHardLoginSession$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createHardLoginSession),
			getOneAndWaitforLoaded(this.store),
			tap((action) => this.logService.info('Effect: createHardLoginSession', action)),
			concatLatestFrom(() => this.store.select(selectAuthLoginState)),
			switchMap(([action, authLoginState]) =>
				this.sessionService
					.CreateSession(
						action.params,
						authLoginState,
						action.oldSessionID,
						action.url,
						action.referrer
					)
					.pipe(
						map((sessionData) => createHardLoginSessionSuccess({ sessionID: sessionData.sessionID })),
						catchError((error: any) => {
							const errorMessage: ErrorMessage = decodeError(error, this.translate);
							return of(createHardLoginSessionFailure({ error: errorMessage }));
						})
					)
			)
		)
	);


  createHardLoginSessionSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createHardLoginSessionSuccess),
				tap((sessionData) => {
					this.logService.info('Effect: createHardLoginSessionSuccess');
					this.storageService.setItem('SESSIONID', sessionData.sessionID);
          // create click_log on login success
					const clickLog: ClickLog = <ClickLog>{};
					clickLog.clickLogOperationID = ClickOperation.Login;
					clickLog.content = '';
					clickLog.message = 'Login OK';
					clickLog.method = 'POST';
					clickLog.outcome = true;
					this.store.dispatch(saveClickLog({ clickLog }));
				})
			),
      { dispatch: false }
	);

	createHardLoginSessionFailure = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createHardLoginSessionFailure),
				tap(() => this.logService.info('Effect: createHardLoginSessionFailure', undefined))
			),
		{ dispatch: false }
	);


	// Session follows identity: whenever identity settles (guest/soft/hard) and no session
	// exists (sessionID === 0) and none is already being created, (re)create it. This restores
	// the invariant broken by recovery paths that resetSession() without recreating one (e.g.
	// authLogoutAndGuest on a Code 17), which otherwise left onlyWhenReady wedged at sessionID=0
	// forever. The sessionCreating guard prevents a double-create: interactive hard/soft login
	// dispatch createHardLoginSession (→ sessionCreating=true) concurrently with loadUser, and if
	// loadUser's authUserLoadedHard lands before the session HTTP returns, this effect would
	// otherwise fire a second createSession. It fires only in the genuine recovery gap, where
	// nothing else is creating a session.
	ensureSessionOnIdentity$ = createEffect(() =>
		this.actions$.pipe(
			ofType(authUserLoadedGuest, authUserLoadedSoft, authUserLoadedHard),
			onlyWhenOnline(this.store),
			concatLatestFrom(() => [
				this.store.select(selectSessionID),
				this.store.select(selectSessionCreating)
			]),
			filter(([, sessionID, sessionCreating]) => sessionID === 0 && !sessionCreating),
			map(() => {
				this.logService.info('Effect: ensureSessionOnIdentity - recreating session (sessionID=0)');
				return this.buildCreateSession();
			})
		)
	);

	// A session-level 401 recovery (call.service) established a fresh server session guid; start a
	// NEW analytics visit chained onto the current sessionID (old→new, never through 0 → no
	// onlyWhenReady wedge). Unlike ensureSessionOnIdentity$ this fires with sessionID > 0.
	regenerateSession$ = createEffect(() =>
		this.actions$.pipe(
			ofType(regenerateSession),
			onlyWhenOnline(this.store),
			concatLatestFrom(() => this.store.select(selectSessionCreating)),
			// Coalesce a burst: N parallel session-level 401 recoveries (e.g. pageview + content +
			// widgets all 401 at once) each dispatch regenerateSession; start only ONE new session —
			// the rest skip while it is in flight (sessionCreating). Avoids redundant click_master rows.
			filter(([, sessionCreating]) => !sessionCreating),
			map(() => {
				this.logService.info('Effect: regenerateSession - starting a new analytics visit');
				return this.buildCreateSession();
			})
		)
	);

	// Shared createSession payload: url + query params from the current location, referrer (browser
	// only), and the current SESSIONID as oldSessionID (chaining old→new).
	private buildCreateSession(): ReturnType<typeof createSession> {
		const url: string = this.location.path(true); // includes fragment
		const params = getQueryParametersFromURL(url);
		let oldSessionID = <number>this.storageService.getItem('SESSIONID');
		if (oldSessionID === null) {
			oldSessionID = 0;
		}
		const referrer: string = isPlatformBrowser(this.platformId) ? this.document.referrer : '';
		return createSession({ params, oldSessionID, url, referrer });
	}

	constructor(
		private actions$: Actions,
		private store: Store,
		private logService: LogService,
		private sessionService: SessionService,
		private storageService: LocalStorageService,
    private translate: TranslateService,
    private location: Location,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
	) {}
}
